import type { JSX } from 'react';
import type { ClientResource, PricingRuleBodyInput } from '@voxler/contracts';
import { assertNever } from '@voxler/domain';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { useState } from 'react';

import { RuleStatusBadge, SheetSection } from './shared';
import { useToast } from '../toast';
import { internalFetch, internalSend, validationDetail } from '../../lib/api';
import { datetimeLocalToIso, formatEffectiveFrom } from '../../lib/format';
import {
  DEFAULT_RULE_TYPE,
  DEFAULT_UNIT,
  describeRule,
  EURO_AMOUNT_PATTERN,
  type PricingRule,
  type PricingUnit,
  ruleStatus,
  type RuleType,
  SMS_UNIT_HINT,
  UNIT_OPTION_LABELS,
  type UseCase,
  unitsForKind,
} from '../../lib/use-cases';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHead,
} from '@/components/ui/dialog';
import { Field, FieldError, FieldHint, Fields2, InputGroup } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { NativeSelect } from '@/components/ui/native-select';
import {
  Table,
  TableBody,
  TableCell,
  TableEmptyRow,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { EmptyState, LoadingState } from '@/components/ui/empty';

/** Scope of a pricing-rule form: the default rule, or an override for a client. */
type RuleScope = 'default' | 'client';

/**
 * Pricing rules of a use case (PRD 8.3): the default rule and the client
 * overrides, each with its history of versions; a new version is added in a
 * dialog (scope, effective date, rule).
 */
export function PricingRulesPanel({
  useCase,
  clients,
  clientName,
}: {
  useCase: UseCase;
  clients: readonly ClientResource[];
  clientName: (clientId: string | null) => string;
}): JSX.Element {
  const queryClient = useQueryClient();
  const { notify } = useToast();

  const [open, setOpen] = useState(false);
  const [scope, setScope] = useState<RuleScope>('default');
  const [clientId, setClientId] = useState('');
  const [effectiveFrom, setEffectiveFrom] = useState('');
  const [ruleType, setRuleType] = useState<RuleType>(DEFAULT_RULE_TYPE[useCase.kind]);
  const [coefficient, setCoefficient] = useState('');
  const [amount, setAmount] = useState('');
  const [unit, setUnit] = useState<PricingUnit>(DEFAULT_UNIT[useCase.kind]);
  const [formError, setFormError] = useState('');

  const units = unitsForKind(useCase.kind);

  const rules = useQuery({
    queryKey: ['pricing-rules', useCase.id],
    queryFn: () =>
      internalFetch<{ rules: PricingRule[] }>(`/internal/use-cases/${useCase.id}/pricing-rules`),
  });

  const resetForm = (): void => {
    setScope('default');
    setClientId('');
    setEffectiveFrom('');
    setRuleType(DEFAULT_RULE_TYPE[useCase.kind]);
    setCoefficient('');
    setAmount('');
    setUnit(DEFAULT_UNIT[useCase.kind]);
    setFormError('');
  };

  const openForm = (initialScope: RuleScope): void => {
    resetForm();
    setScope(initialScope);
    setOpen(true);
  };

  const close = (): void => {
    setOpen(false);
    resetForm();
  };

  const buildRule = (): PricingRuleBodyInput => {
    switch (ruleType) {
      case 'multiplier':
        return { type: 'multiplier', coefficient: coefficient.trim() };
      case 'fixed_per_request':
        return { type: 'fixed_per_request', amount: amount.trim() };
      case 'per_unit':
        return { type: 'per_unit', unit, amount: amount.trim() };
      default:
        return assertNever(ruleType);
    }
  };

  const addVersion = useMutation({
    mutationFn: () => {
      const iso = datetimeLocalToIso(effectiveFrom);
      return internalSend<PricingRule>('POST', `/internal/use-cases/${useCase.id}/pricing-rules`, {
        clientId: scope === 'client' ? clientId : null,
        effectiveFrom: iso,
        rule: buildRule(),
      });
    },
    onSuccess: () => {
      close();
      void queryClient.invalidateQueries({ queryKey: ['pricing-rules', useCase.id] });
      notify('Nouvelle version de prix enregistrée');
    },
    onError: (error) => {
      setFormError(validationDetail(error) ?? 'Échec de l’enregistrement de la version.');
    },
  });

  const submit = (): void => {
    setFormError('');
    if (datetimeLocalToIso(effectiveFrom) === null) {
      setFormError('Date d’effet requise.');
      return;
    }
    if (scope === 'client' && clientId.length === 0) {
      setFormError('Sélectionnez un client pour la surcharge.');
      return;
    }
    if (ruleType === 'multiplier') {
      if (!/^\d+(\.\d+)?$/.test(coefficient.trim())) {
        setFormError('Coefficient invalide (nombre décimal attendu).');
        return;
      }
    } else if (!EURO_AMOUNT_PATTERN.test(amount.trim())) {
      setFormError('Montant invalide (euros, ex. 0,05 → 0.05).');
      return;
    }
    addVersion.mutate();
  };

  const allRules = rules.data?.rules ?? [];
  const sorted = [...allRules].sort(
    (a, b) => new Date(a.effectiveFrom).getTime() - new Date(b.effectiveFrom).getTime(),
  );
  const defaultRules = sorted.filter((rule) => rule.clientId === null);
  const overrideRules = sorted.filter((rule) => rule.clientId !== null);
  const now = Date.now();

  const ruleTable = (list: PricingRule[], scopeHeader: string, empty: string): JSX.Element => (
    <Card>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{scopeHeader}</TableHead>
            <TableHead>Règle (HT)</TableHead>
            <TableHead>Statut</TableHead>
            <TableHead kind="num">Date d’effet</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {list.map((rule) => (
            <TableRow key={rule.id}>
              <TableCell kind="name">
                {rule.clientId === null ? 'Par défaut' : clientName(rule.clientId)}
              </TableCell>
              <TableCell kind="mono">{describeRule(rule.rule)}</TableCell>
              <TableCell>
                <RuleStatusBadge status={ruleStatus(rule, allRules, now)} />
              </TableCell>
              <TableCell kind="num">{formatEffectiveFrom(rule.effectiveFrom)}</TableCell>
            </TableRow>
          ))}
          {list.length === 0 ? <TableEmptyRow colSpan={4}>{empty}</TableEmptyRow> : null}
        </TableBody>
      </Table>
    </Card>
  );

  return (
    <>
      <SheetSection
        title="Règle par défaut"
        description="Historique des versions"
        actions={
          <Button
            type="button"
            size="sm"
            onClick={() => {
              openForm('default');
            }}
          >
            <Plus aria-hidden="true" />
            Nouvelle version
          </Button>
        }
      >
        {rules.isLoading ? (
          <Card>
            <LoadingState />
          </Card>
        ) : (
          ruleTable(defaultRules, 'Portée', 'Aucune règle par défaut.')
        )}
      </SheetSection>

      <SheetSection
        title="Surcharges client"
        description="Prix négocié propre à un client, prioritaire sur la règle par défaut"
        actions={
          overrideRules.length > 0 ? (
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => {
                openForm('client');
              }}
            >
              Ajouter une surcharge
            </Button>
          ) : undefined
        }
      >
        {rules.isLoading ? (
          <Card>
            <LoadingState />
          </Card>
        ) : overrideRules.length === 0 ? (
          <Card>
            <EmptyState
              title="Aucune surcharge client"
              description="Chaque client est facturé à la règle par défaut."
              action={
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    openForm('client');
                  }}
                >
                  Ajouter une surcharge
                </Button>
              }
            />
          </Card>
        ) : (
          ruleTable(overrideRules, 'Client', 'Aucune surcharge client.')
        )}
      </SheetSection>

      <Dialog
        open={open}
        onOpenChange={(next) => {
          if (!next) {
            close();
          }
        }}
      >
        <DialogContent>
          <DialogHead
            title="Nouvelle version de prix"
            description={
              <>
                Cas d’usage <span className="font-mono">{useCase.key}</span>
              </>
            }
          />
          <DialogBody>
            <Fields2>
              <Field label="Portée">
                <NativeSelect
                  value={scope}
                  onChange={(event) => {
                    switch (event.target.value) {
                      case 'default':
                        setScope('default');
                        setClientId('');
                        break;
                      case 'client':
                        setScope('client');
                        break;
                      default:
                        break;
                    }
                  }}
                >
                  <option value="default">Règle par défaut</option>
                  <option value="client">Surcharge client</option>
                </NativeSelect>
              </Field>
              {scope === 'client' ? (
                <Field label="Client" required>
                  <NativeSelect
                    value={clientId}
                    onChange={(event) => {
                      setClientId(event.target.value);
                    }}
                  >
                    <option value="">Choisir un client</option>
                    {clients.map((client) => (
                      <option key={client.id} value={client.id}>
                        {client.name}
                      </option>
                    ))}
                  </NativeSelect>
                </Field>
              ) : null}
            </Fields2>
            <Field label="Date d’effet" required>
              <Input
                type="datetime-local"
                value={effectiveFrom}
                onChange={(event) => {
                  setEffectiveFrom(event.target.value);
                }}
              />
            </Field>
            <Field label="Type de règle">
              <NativeSelect
                value={ruleType}
                onChange={(event) => {
                  switch (event.target.value) {
                    case 'multiplier':
                    case 'fixed_per_request':
                    case 'per_unit':
                      setRuleType(event.target.value);
                      break;
                    default:
                      break;
                  }
                }}
              >
                <option value="multiplier">Multiplicateur</option>
                <option value="fixed_per_request">Forfait par requête</option>
                <option value="per_unit">Par unité</option>
              </NativeSelect>
            </Field>
            {ruleType === 'multiplier' ? (
              <Field label="Coefficient" required>
                <Input
                  numeric
                  value={coefficient}
                  onChange={(event) => {
                    setCoefficient(event.target.value);
                  }}
                  placeholder="1.3"
                  inputMode="decimal"
                />
              </Field>
            ) : (
              <Fields2>
                {ruleType === 'per_unit' ? (
                  <Field label="Unité">
                    <NativeSelect
                      value={unit}
                      onChange={(event) => {
                        const next = units.find((option) => option === event.target.value);
                        if (next !== undefined) {
                          setUnit(next);
                        }
                      }}
                    >
                      {units.map((option) => (
                        <option key={option} value={option}>
                          {UNIT_OPTION_LABELS[option]}
                        </option>
                      ))}
                    </NativeSelect>
                  </Field>
                ) : null}
                <Field label="Montant HT" required>
                  <InputGroup suffix="€">
                    <Input
                      numeric
                      value={amount}
                      onChange={(event) => {
                        setAmount(event.target.value);
                      }}
                      placeholder="0.05"
                      inputMode="decimal"
                    />
                  </InputGroup>
                </Field>
              </Fields2>
            )}
            {ruleType === 'per_unit' && unit === 'sms' ? (
              <FieldHint>{SMS_UNIT_HINT}</FieldHint>
            ) : null}
            {formError.length > 0 ? <FieldError>{formError}</FieldError> : null}
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={close}>
              Annuler
            </Button>
            <Button type="button" size="lg" onClick={submit} disabled={addVersion.isPending}>
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
